import { useState, useEffect, useMemo, useCallback } from 'react'
import { referralService, templateService } from '../services/AdminServices'
import {
  Gift, Plus, Trash2, Save, X, Edit3, AlertTriangle,
  Mail, MessageSquare, Copy,
  FileText, Send, Tag, Eye
} from 'lucide-react'
import './MoreHub.css'

// ═══════════════════════════════════════════════════════════
// MAIN PAGE — 2 tabs: Referidos, Templates
// ═══════════════════════════════════════════════════════════

export default function MoreHub() {
  const [tab, setTab] = useState('referrals')

  return (
    <div className="more-page">
      <div className="more-header">
        <div className="more-header-left">
          <Gift size={28} />
          <div>
            <h1>Herramientas</h1>
            <p className="more-subtitle">Referidos y templates de comunicación</p>
          </div>
        </div>
      </div>

      <div className="more-tabs">
        <button className={tab === 'referrals' ? 'active' : ''} onClick={() => setTab('referrals')}>
          <Gift size={16} /> Referidos
        </button>
        <button className={tab === 'templates' ? 'active' : ''} onClick={() => setTab('templates')}>
          <FileText size={16} /> Templates
        </button>
      </div>

      {tab === 'referrals' && <ReferralsSection />}
      {tab === 'templates' && <TemplatesSection />}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════
// REFERRALS SECTION
// ═══════════════════════════════════════════════════════════

const REFERRAL_STATUSES = [
  { value: 'pending', label: 'Pendiente', color: '#f59e0b' },
  { value: 'contacted', label: 'Contactado', color: '#60a5fa' },
  { value: 'converted', label: 'Convertido', color: '#4ade80' },
  { value: 'rejected', label: 'Rechazado', color: '#f87171' },
]

function ReferralsSection() {
  const [referrals, setReferrals] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState({
    referrerName: '', referrerClientId: '',
    referredName: '', referredEmail: '',
    status: 'pending', reward: '', notes: ''
  })

  const loadReferrals = useCallback(async () => {
    try {
      setLoading(true)
      const res = await referralService.list()
      setReferrals(res.referrals || res.documents || [])
    } catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { loadReferrals() }, [loadReferrals])

  const stats = useMemo(() => {
    const total = referrals.length
    const converted = referrals.filter(r => r.status === 'converted').length
    const pending = referrals.filter(r => r.status === 'pending').length
    const rate = total > 0 ? ((converted / total) * 100).toFixed(0) : 0
    return { total, converted, pending, rate }
  }, [referrals])

  function openForm(ref) {
    if (ref) {
      setEditingId(ref.$id)
      setForm({
        referrerName: ref.referrerName || '', referrerClientId: ref.referrerClientId || '',
        referredName: ref.referredName || '', referredEmail: ref.referredEmail || '',
        status: ref.status || 'pending', reward: ref.reward || '', notes: ref.notes || ''
      })
    } else {
      setEditingId(null)
      setForm({ referrerName: '', referrerClientId: '', referredName: '', referredEmail: '', status: 'pending', reward: '', notes: '' })
    }
    setShowForm(true)
  }

  async function saveReferral() {
    if (!form.referrerName.trim() || !form.referredName.trim()) { setError('Nombres requeridos'); return }
    try {
      if (editingId) await referralService.update(editingId, form)
      else await referralService.create(form)
      setShowForm(false)
      setEditingId(null)
      loadReferrals()
    } catch (e) { setError(e.message) }
  }

  async function deleteReferral(id) {
    if (!confirm('¿Eliminar este referido?')) return
    try { await referralService.delete(id); loadReferrals() } catch (e) { setError(e.message) }
  }

  async function updateStatus(ref, newStatus) {
    try {
      const data = { status: newStatus }
      if (newStatus === 'converted') data.convertedAt = new Date().toISOString()
      await referralService.update(ref.$id, data)
      loadReferrals()
    } catch (e) { setError(e.message) }
  }

  return (
    <>
      {error && <div className="more-error"><AlertTriangle size={16} /> {error} <button onClick={() => setError(null)}><X size={14} /></button></div>}

      <div className="more-stats">
        <div className="more-stat"><span className="more-stat-num">{stats.total}</span><span className="more-stat-label">Total</span></div>
        <div className="more-stat"><span className="more-stat-num" style={{ color: '#4ade80' }}>{stats.converted}</span><span className="more-stat-label">Convertidos</span></div>
        <div className="more-stat"><span className="more-stat-num" style={{ color: '#f59e0b' }}>{stats.pending}</span><span className="more-stat-label">Pendientes</span></div>
        <div className="more-stat"><span className="more-stat-num" style={{ color: '#60a5fa' }}>{stats.rate}%</span><span className="more-stat-label">Conversión</span></div>
      </div>

      <div className="more-toolbar">
        <button className="more-btn more-btn-primary" onClick={() => openForm()}>
          <Plus size={16} /> Nuevo Referido
        </button>
      </div>

      {loading ? (
        <div className="more-loading"><div className="more-spinner" /> Cargando...</div>
      ) : referrals.length === 0 ? (
        <div className="more-empty"><Gift size={48} /><p>No hay referidos registrados</p></div>
      ) : (
        <table className="more-table">
          <thead>
            <tr><th>Referidor</th><th>Referido</th><th>Email</th><th>Estado</th><th>Recompensa</th><th>Acciones</th></tr>
          </thead>
          <tbody>
            {referrals.map(ref => {
              const statusInfo = REFERRAL_STATUSES.find(s => s.value === ref.status) || REFERRAL_STATUSES[0]
              return (
                <tr key={ref.$id}>
                  <td style={{ fontWeight: 600 }}>{ref.referrerName}</td>
                  <td>{ref.referredName}</td>
                  <td style={{ color: '#94a3b8' }}>{ref.referredEmail || '—'}</td>
                  <td>
                    <select className="more-status-select" value={ref.status}
                      style={{ color: statusInfo.color, borderColor: statusInfo.color }}
                      onChange={e => updateStatus(ref, e.target.value)}>
                      {REFERRAL_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                    </select>
                  </td>
                  <td>{ref.reward || '—'}</td>
                  <td className="more-actions">
                    <button onClick={() => openForm(ref)}><Edit3 size={14} /></button>
                    <button onClick={() => deleteReferral(ref.$id)}><Trash2 size={14} /></button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      {showForm && (
        <div className="more-form-overlay">
          <div className="more-form">
            <div className="more-form-header">
              <h2>{editingId ? 'Editar Referido' : 'Nuevo Referido'}</h2>
              <button onClick={() => setShowForm(false)}><X size={20} /></button>
            </div>
            <div className="more-form-body">
              <div className="more-form-row-2col">
                <div className="more-form-row"><label>Nombre del Referidor *</label>
                  <input value={form.referrerName} onChange={e => setForm({ ...form, referrerName: e.target.value })} />
                </div>
                <div className="more-form-row"><label>Nombre del Referido *</label>
                  <input value={form.referredName} onChange={e => setForm({ ...form, referredName: e.target.value })} />
                </div>
              </div>
              <div className="more-form-row-2col">
                <div className="more-form-row"><label>Email del Referido</label>
                  <input value={form.referredEmail} onChange={e => setForm({ ...form, referredEmail: e.target.value })} />
                </div>
                <div className="more-form-row"><label>Recompensa</label>
                  <input value={form.reward} onChange={e => setForm({ ...form, reward: e.target.value })}
                    placeholder="Ej: 10% descuento, 1 mes gratis" />
                </div>
              </div>
              <div className="more-form-row"><label>Notas</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} />
              </div>
            </div>
            <div className="more-form-footer">
              <button className="more-btn more-btn-secondary" onClick={() => setShowForm(false)}><X size={16} /> Cancelar</button>
              <button className="more-btn more-btn-primary" onClick={saveReferral}><Save size={16} /> {editingId ? 'Guardar' : 'Crear'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ═══════════════════════════════════════════════════════════
// TEMPLATES SECTION
// ═══════════════════════════════════════════════════════════

const CHANNELS = ['email', 'whatsapp', 'sms']
const CATEGORIES = ['bienvenida', 'cobranza', 'soporte', 'seguimiento', 'promocion', 'otro']


function TemplatesSection() {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [previewId, setPreviewId] = useState(null)
  const [form, setForm] = useState({
    name: '', channel: 'whatsapp', category: 'bienvenida',
    subject: '', content: '', variables: ''
  })

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true)
      const res = await templateService.list()
      setTemplates(res.templates || res.documents || [])
    } catch (e) { setError(e.message) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { loadTemplates() }, [loadTemplates])

  function openForm(tpl) {
    if (tpl) {
      setEditingId(tpl.$id)
      setForm({
        name: tpl.name || '', channel: tpl.channel || 'whatsapp', category: tpl.category || 'otro',
        subject: tpl.subject || '', content: tpl.content || '', variables: tpl.variables || ''
      })
    } else {
      setEditingId(null)
      setForm({ name: '', channel: 'whatsapp', category: 'bienvenida', subject: '', content: '', variables: '' })
    }
    setShowForm(true)
  }

  async function saveTemplate() {
    if (!form.name.trim() || !form.content.trim()) { setError('Nombre y contenido requeridos'); return }
    try {
      if (editingId) await templateService.update(editingId, form)
      else await templateService.create(form)
      setShowForm(false)
      setEditingId(null)
      loadTemplates()
    } catch (e) { setError(e.message) }
  }

  async function deleteTemplate(id) {
    if (!confirm('¿Eliminar este template?')) return
    try { await templateService.delete(id); loadTemplates() } catch (e) { setError(e.message) }
  }

  function copyContent(content) {
    navigator.clipboard.writeText(content)
  }

  const channelIcon = (ch) => ch === 'email' ? <Mail size={14} /> : ch === 'whatsapp' ? <MessageSquare size={14} /> : <Send size={14} />
  const channelLabel = (ch) => ch === 'email' ? 'Email' : ch === 'whatsapp' ? 'WhatsApp' : 'SMS'

  return (
    <>
      {error && <div className="more-error"><AlertTriangle size={16} /> {error} <button onClick={() => setError(null)}><X size={14} /></button></div>}

      <div className="more-toolbar">
        <span style={{ color: '#94a3b8', fontSize: '0.85rem' }}>{templates.length} templates</span>
        <button className="more-btn more-btn-primary" onClick={() => openForm()}>
          <Plus size={16} /> Nuevo Template
        </button>
      </div>

      {loading ? (
        <div className="more-loading"><div className="more-spinner" /> Cargando...</div>
      ) : templates.length === 0 ? (
        <div className="more-empty"><FileText size={48} /><p>No hay templates creados</p></div>
      ) : (
        <div className="tpl-grid">
          {templates.map(tpl => (
            <div key={tpl.$id} className="tpl-card">
              <div className="tpl-card-header">
                <span className="tpl-channel">{channelIcon(tpl.channel)} {channelLabel(tpl.channel)}</span>
                <span className="tpl-category">{tpl.category}</span>
              </div>
              <div className="tpl-card-title">{tpl.name}</div>
              {tpl.subject && <div className="tpl-card-subject">Asunto: {tpl.subject}</div>}
              <div className="tpl-card-preview">
                {previewId === tpl.$id ? tpl.content : (tpl.content || '').substring(0, 100) + (tpl.content?.length > 100 ? '...' : '')}
              </div>
              {tpl.variables && <div className="tpl-card-vars"><Tag size={12} /> Variables: {tpl.variables}</div>}
              <div className="tpl-card-actions">
                <button onClick={() => setPreviewId(previewId === tpl.$id ? null : tpl.$id)} title="Ver completo">
                  <Eye size={14} />
                </button>
                <button onClick={() => copyContent(tpl.content)} title="Copiar"><Copy size={14} /></button>
                <button onClick={() => openForm(tpl)} title="Editar"><Edit3 size={14} /></button>
                <button onClick={() => deleteTemplate(tpl.$id)} title="Eliminar"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="more-form-overlay">
          <div className="more-form">
            <div className="more-form-header">
              <h2>{editingId ? 'Editar Template' : 'Nuevo Template'}</h2>
              <button onClick={() => setShowForm(false)}><X size={20} /></button>
            </div>
            <div className="more-form-body">
              <div className="more-form-row"><label>Nombre *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Ej: Bienvenida nuevo cliente" />
              </div>
              <div className="more-form-row-2col">
                <div className="more-form-row"><label>Canal</label>
                  <select value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })}>
                    {CHANNELS.map(c => <option key={c} value={c}>{channelLabel(c)}</option>)}
                  </select>
                </div>
                <div className="more-form-row"><label>Categoría</label>
                  <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
                  </select>
                </div>
              </div>
              {form.channel === 'email' && (
                <div className="more-form-row"><label>Asunto</label>
                  <input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} />
                </div>
              )}
              <div className="more-form-row"><label>Contenido *</label>
                <textarea value={form.content} onChange={e => setForm({ ...form, content: e.target.value })}
                  placeholder="Usá {{nombre}}, {{plan}}, etc. para variables" rows={8} />
              </div>
              <div className="more-form-row"><label>Variables (separadas por coma)</label>
                <input value={form.variables} onChange={e => setForm({ ...form, variables: e.target.value })}
                  placeholder="Ej: nombre, plan, dispositivo" />
              </div>
            </div>
            <div className="more-form-footer">
              <button className="more-btn more-btn-secondary" onClick={() => setShowForm(false)}><X size={16} /> Cancelar</button>
              <button className="more-btn more-btn-primary" onClick={saveTemplate}><Save size={16} /> {editingId ? 'Guardar' : 'Crear'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// NotificationsSection was moved to NotificationsHub.jsx
